import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import AppLayout from '@/components/feature/AppLayout';
import { complianceApi } from '@/api/compliance.api';
import { useToast } from '@/contexts/ToastContext';
import { buildEInvoiceQRText } from '@/utils/gstCalculation';
import {
  calculateEInvoiceEligibility,
  formatINR,
  formatCr,
  E_INVOICE_TURNOVER_THRESHOLD,
} from '@/utils/eInvoiceEligibility';

// Quick Preset Sandbox Accounts for E-Invoice Testing
const SANDBOX_STATE_ACCOUNTS = [
  {
    state: 'Karnataka (29)',
    gstin: '29AAGCB1286Q000',
    user: 'BVMGSP',
    pass: 'Wbooks@0142',
    clientId: 'EINS0500ce3d-dfd1-4e73-8613-9a423478bc4c',
    clientSecret: 'EINSc680b203-5886-4048-bcab-614c21f72cfa',
  },
  {
    state: 'Maharashtra (27)',
    gstin: '27AAGCB1286Q005',
    user: 'BVMMH',
    pass: 'Bvm@123456',
    clientId: 'EWBSd56ba380-ad4a-4db1-b43f-6ab5b7870918',
    clientSecret: 'EWBS3f2d7a66-ebef-4457-81ea-21cb12ff9e84',
  },
];

export default function EInvoiceSettingsPage() {
  const navigate = useNavigate();
  const toast = useToast();

  // Company Turnover State
  const [companyTurnover, setCompanyTurnover] = useState<{
    previousFyTurnover: number;
    currentFyEstTurnover: number;
    einvoiceEverApplicable: boolean;
    einvoiceManualOverride: boolean;
    einvoiceApplicable: boolean;
    status: string;
    reason: string;
    oneWayGateActive: boolean;
  }>({
    previousFyTurnover: 0,
    currentFyEstTurnover: 0,
    einvoiceEverApplicable: false,
    einvoiceManualOverride: false,
    einvoiceApplicable: false,
    status: 'EXEMPT',
    reason: '',
    oneWayGateActive: false,
  });

  // Settings state
  const [enableEInvoice, setEnableEInvoice] = useState(true);
  const [financialYear, setFinancialYear] = useState('2025-26');
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [gstin, setGstin] = useState('29AAGCB1286Q000');
  const [apiUsername, setApiUsername] = useState('BVMGSP');
  const [apiPassword, setApiPassword] = useState('Wbooks@0142');
  const [showPassword, setShowPassword] = useState(false);
  const [clientId, setClientId] = useState('EINS0500ce3d-dfd1-4e73-8613-9a423478bc4c');
  const [clientSecret, setClientSecret] = useState('EINSc680b203-5886-4048-bcab-614c21f72cfa');
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('ankitbowade3731@gmail.com');

  // Automation Settings
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [skipB2C, setSkipB2C] = useState(true);
  const [alertOnFailure, setAlertOnFailure] = useState(true);

  // QR Code & Print Display Settings
  const [qrSize, setQrSize] = useState<'60' | '80' | '100'>('80');
  const [qrPosition, setQrPosition] = useState<'bottom_right' | 'bottom_left' | 'top_right'>('bottom_right');
  const [showIRN, setShowIRN] = useState(true);
  const [showAckNumber, setShowAckNumber] = useState(true);
  const [showQRCode, setShowQRCode] = useState(true);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectionTesting, setConnectionTesting] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // 1. Load Company Turnover compliance settings
        const compRes = await complianceApi.getCompanySettings().catch(() => null);
        if (compRes?.success && compRes?.data) {
          const cd = compRes.data;
          setCompanyTurnover({
            previousFyTurnover: cd.previousFyTurnover || 0,
            currentFyEstTurnover: cd.currentFyEstTurnover || 0,
            einvoiceEverApplicable: Boolean(cd.einvoiceEverApplicable),
            einvoiceManualOverride: Boolean(cd.einvoiceManualOverride),
            einvoiceApplicable: Boolean(cd.einvoiceApplicable),
            status: cd.status || 'EXEMPT',
            reason: cd.reason || '',
            oneWayGateActive: Boolean(cd.oneWayGateActive),
          });
        }

        // 2. Load E-Invoice settings
        const res = await complianceApi.getEInvoiceSettings().catch(() => null);
        if (res?.success && res?.data) {
          const d = res.data;
          setEnvironment(d.environment === 'production' ? 'production' : 'sandbox');
          if (d.apiUsername) setApiUsername(d.apiUsername);
          if (d.apiPassword) setApiPassword(d.apiPassword);
          if (d.gstin) setGstin(d.gstin);
          if (d.clientId) setClientId(d.clientId);
          if (d.clientSecret) setClientSecret(d.clientSecret);
          if (d.emailRecipients) setEmailRecipients(d.emailRecipients);
          if (d.qrSize) setQrSize(d.qrSize as any);
          if (d.qrPosition) setQrPosition(d.qrPosition as any);
          if (d.showIrn !== undefined) setShowIRN(d.showIrn);
          if (d.showAckNumber !== undefined) setShowAckNumber(d.showAckNumber);
          if (d.showQrCode !== undefined) setShowQRCode(d.showQrCode);
          if (d.autoGenerate !== undefined) setAutoGenerate(d.autoGenerate);
          if (d.skipB2c !== undefined) setSkipB2C(d.skipB2c);
          if (d.alertOnFailure !== undefined) setAlertOnFailure(d.alertOnFailure);
        } else {
          // Fallback to E-Way bill settings for credentials
          const ewbRes = await complianceApi.getEWayBillSettings().catch(() => null);
          if (ewbRes?.success && ewbRes?.data) {
            const d = ewbRes.data;
            if (d.apiUsername) setApiUsername(d.apiUsername);
            if (d.apiPassword) setApiPassword(d.apiPassword);
            if (d.gstin) setGstin(d.gstin);
            if (d.clientId) setClientId(d.clientId);
            if (d.clientSecret) setClientSecret(d.clientSecret);
          }
        }
      } catch (err) {
        console.error('Failed to load E-Invoice settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleSelectAccount = (acc: typeof SANDBOX_STATE_ACCOUNTS[0]) => {
    setGstin(acc.gstin);
    setApiUsername(acc.user);
    setApiPassword(acc.pass);
    if (acc.clientId) setClientId(acc.clientId);
    if (acc.clientSecret) setClientSecret(acc.clientSecret);
    toast.info(`Selected ${acc.state} sandbox account`);
  };

  const handleTestConnection = async () => {
    setConnectionTesting(true);
    try {
      const res = await complianceApi.testEInvoiceAuth({
        gstin,
        username: apiUsername,
        password: apiPassword,
        clientId,
        clientSecret,
        environment,
        email: emailRecipients?.split(',')[0]?.trim() || 'ankitbowade3731@gmail.com',
      });

      if (res?.success) {
        toast.success(res.message || 'Authenticated successfully with Whitebooks E-Invoice API ✓');
      } else {
        toast.error(res?.message || 'E-Invoice Authentication failed');
      }
    } catch (err: any) {
      toast.error(err?.message || 'E-Invoice Authentication test failed');
    } finally {
      setConnectionTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        environment,
        apiUsername,
        apiPassword,
        clientId,
        clientSecret,
        gstin,
        emailRecipients,
        qrSize,
        qrPosition,
        showIrn: showIRN,
        showAckNumber,
        showQrCode: showQRCode,
        autoGenerate,
        skipB2c: skipB2C,
        alertOnFailure,
      };

      const res = await complianceApi.updateEInvoiceSettings(payload);
      // Also update general compliance credentials
      await complianceApi.updateEWayBillSettings({
        environment,
        apiUsername,
        apiPassword,
        clientId,
        clientSecret,
        gstin,
        emailRecipients,
      }).catch(() => {});

      if (res?.success) {
        toast.success('E-Invoice & Print Settings Saved Successfully ✓');
      } else {
        toast.error(res?.message || 'Failed to save settings');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2 text-slate-600 text-sm font-medium">
            <i className="ri-loader-4-line animate-spin text-xl text-indigo-600" />
            Loading E-Invoice Settings...
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6 max-w-4xl mx-auto pb-12">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <i className="ri-file-code-line text-indigo-600 text-2xl" />
              E-Invoice Settings
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Configure Whitebooks IRP GSP authentication credentials, automation, and QR code print display
            </p>
          </div>
          <button
            onClick={() => navigate('/compliance/e-invoice')}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Back to E-Invoice
          </button>
        </div>

        {/* Section 0: Company Turnover & Mandate Compliance Overview */}
        <div className={`p-4 rounded-xl border transition-all ${
          companyTurnover.status === 'MANDATORY'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-950'
            : companyTurnover.status === 'MANUAL_OVERRIDE'
            ? 'bg-amber-50 border-amber-200 text-amber-950'
            : 'bg-slate-50 border-slate-200 text-slate-900'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                companyTurnover.status === 'MANDATORY'
                  ? 'bg-emerald-600 text-white'
                  : companyTurnover.status === 'MANUAL_OVERRIDE'
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-600 text-white'
              }`}>
                <i className={`text-base ${
                  companyTurnover.status === 'MANDATORY'
                    ? 'ri-checkbox-circle-fill'
                    : companyTurnover.status === 'MANUAL_OVERRIDE'
                    ? 'ri-toggle-fill'
                    : 'ri-information-fill'
                }`} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Company E-Invoice Applicability:
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    companyTurnover.status === 'MANDATORY'
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                      : companyTurnover.status === 'MANUAL_OVERRIDE'
                      ? 'bg-amber-100 text-amber-800 border border-amber-300'
                      : 'bg-slate-200 text-slate-800 border border-slate-300'
                  }`}>
                    {companyTurnover.status === 'MANDATORY' ? 'MANDATORY (Turnover ≥ ₹5 Cr)' : companyTurnover.status === 'MANUAL_OVERRIDE' ? 'MANDATORY (Admin Override)' : 'EXEMPT (< ₹5 Cr)'}
                  </span>
                  {companyTurnover.oneWayGateActive && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-md border border-emerald-200">
                      <i className="ri-lock-2-fill text-xs" /> One-Way Gate Locked
                    </span>
                  )}
                </div>
                <p className="text-xs font-medium text-slate-600 mt-1">
                  Previous FY Turnover: <strong>{formatCr(companyTurnover.previousFyTurnover)}</strong> (Threshold: ₹5.00 Cr / ₹50,000,000)
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors cursor-pointer self-start sm:self-auto shrink-0 shadow-2xs"
            >
              <i className="ri-edit-line text-xs" />
              Edit Company Turnover
            </button>
          </div>
        </div>

        {/* Section 1: Integration Status Banner */}
        <div className="bg-gradient-to-r from-indigo-900 to-slate-900 text-white rounded-xl p-5 shadow-sm border border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded">
                Whitebooks GSP Active
              </span>
              <span className="text-xs text-slate-300">• API Version v1.03</span>
            </div>
            <p className="text-xs text-slate-300">
              Generate government GST e-invoices with signed IRN, Ack No, and 2D QR codes in real-time.
            </p>
          </div>

          <button
            onClick={handleTestConnection}
            disabled={connectionTesting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-md transition-colors cursor-pointer shrink-0 disabled:opacity-50"
          >
            {connectionTesting ? (
              <><i className="ri-loader-4-line animate-spin text-sm" /> Authenticating...</>
            ) : (
              <><i className="ri-shield-keyhole-line text-sm" /> Authenticate E-Invoice API</>
            )}
          </button>
        </div>

        {/* Section 2: Sandbox State Accounts Presets */}
        <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
              <i className="ri-flask-line text-amber-600 text-base" />
              <span>Whitebooks GSP Sandbox Preset Accounts (Select to Pre-fill)</span>
            </div>
            <span className="text-[11px] text-amber-700 font-medium">Click any state to test connection</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {SANDBOX_STATE_ACCOUNTS.map((acc) => (
              <button
                key={acc.gstin}
                type="button"
                onClick={() => handleSelectAccount(acc)}
                className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer flex items-center justify-between ${
                  gstin === acc.gstin
                    ? 'bg-amber-100/90 border-amber-400 ring-2 ring-amber-500/20'
                    : 'bg-white/80 border-amber-200 hover:bg-white'
                }`}
              >
                <div>
                  <span className="text-xs font-bold text-gray-900 block">{acc.state}</span>
                  <span className="text-[11px] font-mono text-gray-600">{acc.gstin}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-gray-500 font-mono block">user: {acc.user}</span>
                  <span className="text-[10px] font-semibold text-indigo-600">Select →</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Section 3: General E-Invoice Configuration */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
            <i className="ri-[#15235e] ri-settings-3-line text-indigo-600" />
            General E-Invoice Configuration
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block font-semibold text-gray-700 mb-1">
                Company GSTIN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={gstin}
                onChange={(e) => setGstin(e.target.value)}
                placeholder="e.g. 29AAGCB1286Q000"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">Financial Year</label>
              <select
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
              >
                <option value="2024-25">2024-25</option>
                <option value="2025-26">2025-26</option>
                <option value="2026-27">2026-27</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section 4: Whitebooks IRP Credentials */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4 shadow-sm">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <i className="ri-key-2-line text-indigo-600" />
              Whitebooks IRP GSP Credentials
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setEnvironment('sandbox')}
                className={`px-3 py-1 text-xs font-semibold rounded-lg border cursor-pointer transition-all ${
                  environment === 'sandbox'
                    ? 'bg-amber-50 text-amber-700 border-amber-300 ring-2 ring-amber-500/20'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Sandbox Mode
              </button>
              <button
                onClick={() => setEnvironment('production')}
                className={`px-3 py-1 text-xs font-semibold rounded-lg border cursor-pointer transition-all ${
                  environment === 'production'
                    ? 'bg-red-50 text-red-700 border-red-300 ring-2 ring-red-500/20'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Production Mode
              </button>
            </div>
          </div>

          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  API Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={apiUsername}
                  onChange={(e) => setApiUsername(e.target.value)}
                  placeholder="e.g. BVMGSP"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  API Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={apiPassword}
                    onChange={(e) => setApiPassword(e.target.value)}
                    placeholder="e.g. Wbooks@0142"
                    className="w-full px-3 py-2 pr-10 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="e.g. EINS0500ce3d-dfd1-4e73-8613-9a423478bc4c"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Client Secret <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showClientSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="e.g. EINSc680b203-5886-4048-bcab-614c21f72cfa"
                    className="w-full px-3 py-2 pr-10 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowClientSecret(!showClientSecret)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showClientSecret ? 'ri-eye-off-line' : 'ri-eye-line'} />
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block font-semibold text-gray-700 mb-1">GSP Email / Notifications</label>
              <input
                type="email"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                placeholder="e.g. ankitbowade3731@gmail.com"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Section 5: Automation Settings */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
            <i className="ri-magic-line text-indigo-600" />
            Automation Settings
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-semibold text-gray-800">Auto-Generate IRN on Sales Invoice Save</label>
                <p className="text-[11px] text-gray-500">Automatically register IRN when a new tax invoice is saved</p>
              </div>
              <button
                type="button"
                onClick={() => setAutoGenerate(!autoGenerate)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${autoGenerate ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoGenerate ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-semibold text-gray-800">Skip B2C Invoices</label>
                <p className="text-[11px] text-gray-500">E-invoicing applies only to B2B, B2G and Export transactions</p>
              </div>
              <button
                type="button"
                onClick={() => setSkipB2C(!skipB2C)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${skipB2C ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${skipB2C ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-semibold text-gray-800">Alert on Generation Failure</label>
                <p className="text-[11px] text-gray-500">Receive instant toast & email notifications if IRP API fails</p>
              </div>
              <button
                type="button"
                onClick={() => setAlertOnFailure(!alertOnFailure)}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${alertOnFailure ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${alertOnFailure ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Section 6: QR Code & Print Display Settings + Live Interactive Invoice Preview */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5 shadow-sm">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <i className="ri-qr-code-line text-indigo-600" />
              QR Code & Invoice Print Customization
            </h3>
            <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
              Live Interactive Preview
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Controls Column */}
            <div className="lg:col-span-7 space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">QR Code Display Size</label>
                  <select
                    value={qrSize}
                    onChange={(e) => setQrSize(e.target.value as any)}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium"
                  >
                    <option value="60">60px × 60px (Compact)</option>
                    <option value="80">80px × 80px (Standard / Default)</option>
                    <option value="100">100px × 100px (Large / High Visibility)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 mb-1">QR Code Position</label>
                  <select
                    value={qrPosition}
                    onChange={(e) => setQrPosition(e.target.value as any)}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium"
                  >
                    <option value="bottom_right">Bottom Right (Standard)</option>
                    <option value="bottom_left">Bottom Left</option>
                    <option value="top_right">Top Right (Header)</option>
                  </select>
                </div>
              </div>

              {/* Toggles */}
              <div className="pt-2 space-y-3 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-semibold text-gray-800">Print 64-char IRN Hash on Invoice</label>
                    <p className="text-[11px] text-gray-500">Display Government IRN string in the invoice header/metadata</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowIRN(!showIRN)}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${showIRN ? 'bg-indigo-600' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showIRN ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-semibold text-gray-800">Print Ack No & Ack Date</label>
                    <p className="text-[11px] text-gray-500">Show Government Acknowledgment Number and Date</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAckNumber(!showAckNumber)}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${showAckNumber ? 'bg-indigo-600' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showAckNumber ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-semibold text-gray-800">Print B2B 2D QR Code</label>
                    <p className="text-[11px] text-gray-500">Render digital signature QR Code on invoice print view</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowQRCode(!showQRCode)}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${showQRCode ? 'bg-indigo-600' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showQRCode ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Right Live Preview Box */}
            <div className="lg:col-span-5">
              <div className="bg-slate-50 border-2 border-dashed border-indigo-200 rounded-xl p-4 flex flex-col justify-between min-h-[260px] relative shadow-inner">
                <div className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                  Tax Invoice Document
                </div>

                {/* Simulated Header */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded bg-indigo-600 text-white flex items-center justify-center text-[10px] font-bold">
                      D
                    </div>
                    <span className="text-xs font-bold text-slate-800">DIGI INDIA SOLUTIONS</span>
                  </div>

                  {showIRN && (
                    <div className="bg-white border border-slate-200 rounded p-1.5 mb-2 font-mono text-[9px] text-slate-600 break-all leading-tight">
                      <span className="font-bold text-indigo-700">IRN: </span>
                      3a8f9c12b4e57801df92e10a8bc6530182479e0f3d6c1b827e4920a1f56bc402
                    </div>
                  )}

                  {showAckNumber && (
                    <div className="flex gap-2 text-[9px] text-slate-600 font-mono mb-2">
                      <span className="bg-white px-1.5 py-0.5 rounded border border-slate-200">
                        <strong>Ack No:</strong> 122410982341
                      </span>
                      <span className="bg-white px-1.5 py-0.5 rounded border border-slate-200">
                        <strong>Date:</strong> 19/08/2026
                      </span>
                    </div>
                  )}

                  {/* Top Right QR Position */}
                  {qrPosition === 'top_right' && showQRCode && (
                    <div className="absolute top-10 right-3 bg-white p-1.5 border border-indigo-300 rounded-lg shadow-sm text-center flex flex-col items-center">
                      <QRCodeSVG
                        value={buildEInvoiceQRText({
                          sellerGstin: gstin || '29AAGCB1286Q000',
                          buyerGstin: '27ABCDE1234F1Z5',
                          docNo: 'INV-2026-001',
                          docType: 'INV',
                          docDate: '19/08/2026',
                          totInvVal: 150000,
                          itemCnt: 2,
                          mainHsnCode: '8471',
                          irn: '3a8f9c12b4e57801df92e10a8bc6530182479e0f3d6c1b827e4920a1f56bc402',
                          ackNo: '122410982341',
                          ackDate: '19/08/2026',
                        })}
                        size={qrSize === '60' ? 48 : qrSize === '80' ? 64 : 80}
                        level="M"
                      />
                      <span className="text-[8px] font-bold text-indigo-700 mt-1 uppercase">{qrSize}px QR</span>
                    </div>
                  )}

                  {/* Dummy Table Lines */}
                  <div className="space-y-1 mt-3">
                    <div className="h-2 bg-slate-200 rounded w-full" />
                    <div className="h-2 bg-slate-200 rounded w-4/5" />
                    <div className="h-2 bg-slate-200 rounded w-3/4" />
                  </div>
                </div>

                {/* Bottom QR Positions */}
                <div className="mt-4 pt-3 border-t border-slate-200 flex items-end justify-between">
                  {qrPosition === 'bottom_left' && showQRCode ? (
                    <div className="bg-white p-1.5 border border-indigo-300 rounded-lg shadow-sm text-center flex flex-col items-center">
                      <QRCodeSVG
                        value={buildEInvoiceQRText({
                          sellerGstin: gstin || '29AAGCB1286Q000',
                          buyerGstin: '27ABCDE1234F1Z5',
                          docNo: 'INV-2026-001',
                          docType: 'INV',
                          docDate: '19/08/2026',
                          totInvVal: 150000,
                          itemCnt: 2,
                          mainHsnCode: '8471',
                          irn: '3a8f9c12b4e57801df92e10a8bc6530182479e0f3d6c1b827e4920a1f56bc402',
                          ackNo: '122410982341',
                          ackDate: '19/08/2026',
                        })}
                        size={qrSize === '60' ? 48 : qrSize === '80' ? 64 : 80}
                        level="M"
                      />
                      <span className="text-[8px] font-bold text-indigo-700 mt-1 uppercase">{qrSize}px Bottom-Left</span>
                    </div>
                  ) : <div />}

                  {qrPosition === 'bottom_right' && showQRCode && (
                    <div className="bg-white p-1.5 border border-indigo-300 rounded-lg shadow-sm text-center flex flex-col items-center">
                      <QRCodeSVG
                        value={buildEInvoiceQRText({
                          sellerGstin: gstin || '29AAGCB1286Q000',
                          buyerGstin: '27ABCDE1234F1Z5',
                          docNo: 'INV-2026-001',
                          docType: 'INV',
                          docDate: '19/08/2026',
                          totInvVal: 150000,
                          itemCnt: 2,
                          mainHsnCode: '8471',
                          irn: '3a8f9c12b4e57801df92e10a8bc6530182479e0f3d6c1b827e4920a1f56bc402',
                          ackNo: '122410982341',
                          ackDate: '19/08/2026',
                        })}
                        size={qrSize === '60' ? 48 : qrSize === '80' ? 64 : 80}
                        level="M"
                      />
                      <span className="text-[8px] font-bold text-indigo-700 mt-1 uppercase">{qrSize}px Bottom-Right</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 7: Save & Test Action Footer */}
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <button
            onClick={handleTestConnection}
            disabled={connectionTesting}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            {connectionTesting ? (
              <><i className="ri-loader-4-line animate-spin" /> Authenticating with Whitebooks...</>
            ) : (
              <><i className="ri-link" /> Test Whitebooks API Connection</>
            )}
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin" /> Saving...</>
            ) : (
              <><i className="ri-save-line" /> Save Settings</>
            )}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}